import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import CommonBlockSettings from "./CommonBlockSettings";
import { ColorPicker } from "../Block.styled";
import { InputFieldWithLabel } from "../../../../../../components";
import { Typography } from "../../../../../../components/Typography";
import { StateBrandingContext } from "../../../../../context/BrandingContext";
import { InputGroup } from "../../../../../../components/Input/Input";
import { Button, RemoveButton } from "../../../../../../components";
import { PrimaryButton } from "../../../../../../components/Button";
import { Flex } from "../../../../../style";
import { Col, Row } from "../../../../../../components/Grid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Icon } from "../../../../../../components";

// Context
import { PageContext } from "../../../../../context/PageContext";

export const FactsContent = ({ block }) => {
  const { t } = useTranslation();
  const { dispatch } = useContext(PageContext);

  // Parse body as array or start empty
  const bodyArray = (() => {
    try {
      return Array.isArray(block.body) ? block.body : block.body ? JSON.parse(block.body) : [];
    } catch {
      return [];
    }
  })();

  // Add new item
  const handleAdd = () => {
    // Limit to 5 items
    if (bodyArray.length >= 5) {
      return;
    }

    const newBody = [...bodyArray, { label: "", content: "" }];
    dispatch({
      type: "update_block",
      payload: { id: block.id, name: "body", value: JSON.stringify(newBody) },
    });
  };

  // Update item
  const handleChange = (idx, field, value) => {
    const newBody = bodyArray.map((item, i) => (i === idx ? { ...item, [field]: value } : item));
    dispatch({
      type: "update_block",
      payload: { id: block.id, name: "body", value: JSON.stringify(newBody) },
    });
  };

  // Remove item
  const handleRemove = (idx) => {
    const newBody = bodyArray.filter((_, i) => i !== idx);
    dispatch({
      type: "update_block",
      payload: { id: block.id, name: "body", value: JSON.stringify(newBody) },
    });
  };

  // Handle drag end
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(bodyArray);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    dispatch({
      type: "update_block",
      payload: { id: block.id, name: "body", value: JSON.stringify(reordered) },
    });
  };

  return (
    <>
      <InputFieldWithLabel
        block
        tiny
        label={t("component.block.section_header_label", "Section header")}
        byline={t("component.block.section_header_byline", "Choose a header for this section of text")}
        onChange={(e) => {
          dispatch({
            type: "update_block",
            payload: { id: block.id, name: "title", value: e.target.value },
          });
        }}
        name="title"
        type="text"
        value={block.title || ""}
      />

      <InputFieldWithLabel
        block
        tiny
        label={t("component.block.section_facts_source_label", "Source section")}
        byline={t("component.block.section_facts_source_subtitle", "Choose a source for this section of text")}
        onChange={(e) => {
          dispatch({
            type: "update_block",
            payload: { id: block.id, name: "subTitle", value: e.target.value },
          });
        }}
        name="subTitle"
        type="text"
        value={block.subTitle || ""}
      />

      {/* Drag & drop repeater for label/content pairs */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="facts-list" direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                display: "flex",
                flexDirection: "row",
                paddingTop: "26px",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              {bodyArray.map((item, idx) => (
                <Draggable key={idx} draggableId={`fact-${idx}`} index={idx}>
                  {(dragProvided) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      style={{
                        position: "relative",
                        background: "#f9f9f9",
                        padding: "0px 0px 25px 0px",
                        borderRadius: 4,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        width: "calc(20% - 12.8px)", // 5 items with 4 gaps of 16px => (16px * 4 / 5 = 12.8px)
                        minWidth: "350px",
                        ...dragProvided.draggableProps.style,
                      }}
                    >
                      <h3
                        className="fact-index"
                        style={{
                          width: "100%",
                          borderBottom: "1px solid rgb(240, 240, 243)",
                          // paddingBottom: "10px",
                          // paddingTop: "15px",
                          padding: "15px 16px 10px 16px",
                          marginBottom: "10px",
                          color: "#181f38",
                          backgroundColor: "#f3f3f5",
                        }}
                      >
                        {/* {t("component.block.section_facts_fact_title", "Fact")}{" "} */}
                        {idx + 1}
                      </h3>
                      <div style={{ padding: "0 8px 0 16px", flex: 1 }}>
                        <InputFieldWithLabel
                          pt={"0"}
                          pb={"0"}
                          label={t("component.block.section_facts_fact_label", "Label")}
                          name={`label-${idx}`}
                          type="text"
                          value={item.label}
                          onChange={(e) => handleChange(idx, "label", e.target.value)}
                        />
                      </div>
                      <div style={{ padding: "0 16px 0 8px", flex: 1 }}>
                        <InputFieldWithLabel
                          pt={"0"}
                          pb={"0"}
                          label={t("component.block.section_facts_fact_content", "Content")}
                          name={`content-${idx}`}
                          type="text"
                          value={item.content}
                          onChange={(e) => handleChange(idx, "content", e.target.value)}
                        />
                      </div>
                      <RemoveButton type="button" largeIcon width="44px" height="44px" onClick={() => handleRemove(idx)} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Flex justify="flex-end">
        <Row>
          <Col vCenter>
            <PrimaryButton red onClick={handleAdd} disabled={bodyArray.length >= 5} alignCenter mt="25">
              {t("Add fact")}
              <Icon icon="plus" mr="10" />
            </PrimaryButton>
          </Col>
        </Row>
      </Flex>
    </>
  );
};

export const FactsSettings = ({ block }) => {
  const { t } = useTranslation();
  const { dispatch } = useContext(PageContext);
  const [selectedColor, setSelectedColor] = useState(block.backgroundColor || "");
  const { branding } = useContext(StateBrandingContext);

  const colorOptions = [
    { name: "Black", value: "#000000" },
    { name: "White", value: "#FFFFFF" },
    ...[branding.brandingColorOne, branding.brandingColorTwo, branding.brandingColorThree, branding.brandingColorFour]
      .filter((color) => color !== null || false) // Filter out "false" values
      .map((color, index) => ({ key: index, name: color, value: color })),
  ];

  const handleColorChange = (e) => {
    const color = e.target.value;
    setSelectedColor(color);
    dispatch({
      type: "update_block",
      payload: {
        id: block.id,
        name: "backgroundColor",
        value: color,
      },
    });
  };

  block.paddingVertical = block.paddingVertical || "md"; // Default padding

  return (
    <>
      <InputGroup block pt="0">
        <Flex flexDirection="column" alignItems="flex-start" flexDirectionLG="row" alignItemsLG="center" justifyLG="space-between">
          <div>
            <Typography tag="p" mb="4" mbXL="10" bold>
              {t("component.block.background_color_label", "Background color")}
            </Typography>
            <Typography tag="p" lowOpacity mb="22" mbXL="0">
              {t("common.backgroundDesc")}
            </Typography>
          </div>
          <div>
            <Flex plLG="50">
              {colorOptions.map((color, index) => (
                <ColorPicker
                  key={index}
                  type="radio"
                  name="backgroundColor"
                  value={color.value}
                  checked={selectedColor === color.value}
                  onChange={handleColorChange}
                  className={selectedColor === color.value ? "active" : ""}
                  pLabel={color.name}
                />
              ))}
            </Flex>
          </div>
        </Flex>
      </InputGroup>
      <CommonBlockSettings block={block} />
    </>
  );
};
